import { ENEMIES } from '../../data/enemies';
import { GRAVITY, SIM_HZ } from '../../data/tuning';

/**
 * EN-11 Hipopótamo de la Hacienda (GDD §8.3, issue #87): bubbles in its lagoon 1.2 s before it jumps, a
 * ballistic arc with its apex at y = 5 and a splash back into the water. A friendly caricature (GDD §3.4):
 * nobody hurts it and it only hurts while it is in the air. The arc uses the flight gravity (30 u/s2), so it
 * leaves at sqrt(2 * g * 4) = 15.49 u/s and peaks after 31 ticks. Pure and deterministic.
 */
export const EN11 = ENEMIES['EN-11'];

export const BUBBLES_TICKS = Math.round(EN11.bubblesLeadSeconds * SIM_HZ);
/** Vertical speed leaving the lagoon, from the apex height. */
export const LAUNCH_SPEED = Math.sqrt(2 * GRAVITY * (EN11.apexY - EN11.lagoonY));
export const APEX_TICKS = Math.round((LAUNCH_SPEED / GRAVITY) * SIM_HZ);

export type HippoPhase = 'submerged' | 'bubbling' | 'airborne';

export interface HippoState {
  phase: HippoPhase;
  /** Ticks since it was first active (it keeps counting after it leaves the screen). */
  ticks: number;
  /** Tick of the next jump, counted the same way. */
  jumpAt: number;
  /** Ticks since it left the water, while airborne. */
  flight: number;
  /** Seconds between jumps, if the level asks for a cycle. */
  repeatEvery: number | null;
}

/** Per-instance parameters of the level data (`SegmentDef.enemies[].params`). */
export interface HippoParams {
  jumpAtTick?: number;
  repeatEverySeconds?: number;
}

export function createHippo(params: HippoParams = {}): HippoState {
  return {
    phase: 'submerged',
    ticks: 0,
    jumpAt: params.jumpAtTick ?? BUBBLES_TICKS,
    flight: 0,
    repeatEvery: params.repeatEverySeconds ?? null,
  };
}

/** Height after `flight` ticks in the air: a parabola from the lagoon surface. */
export function arcHeight(flight: number): number {
  const t = flight / SIM_HZ;
  return EN11.lagoonY + LAUNCH_SPEED * t - 0.5 * GRAVITY * t * t;
}

export interface HippoStep {
  y: number;
  bubbles: boolean;
  jump: boolean;
  splash: boolean;
}

/** One tick: moves through submerged > bubbling > airborne > submerged and says what just happened. */
export function stepHippo(state: HippoState): HippoStep {
  const result: HippoStep = { y: EN11.lagoonY, bubbles: false, jump: false, splash: false };
  state.ticks += 1;
  if (state.phase === 'airborne') {
    state.flight += 1;
    const y = arcHeight(state.flight);
    if (y <= EN11.lagoonY) {
      state.phase = 'submerged';
      state.flight = 0;
      result.splash = true;
      if (state.repeatEvery !== null) state.jumpAt += Math.round(state.repeatEvery * SIM_HZ);
      return result;
    }
    result.y = y;
    return result;
  }
  if (state.ticks >= state.jumpAt) {
    state.phase = 'airborne';
    state.flight = 0;
    result.jump = true;
    return result;
  }
  if (state.phase === 'submerged' && state.ticks >= state.jumpAt - BUBBLES_TICKS) {
    state.phase = 'bubbling';
    result.bubbles = true;
  }
  return result;
}
